import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { $isRootTextContentEmpty } from '@lexical/text';
import type { SerializedEditorState } from 'lexical';

const initialConfig = {
  namespace: 'StickyNoteEditor',
  onError: (error: Error) => {
    console.error('Lexical error:', error);
  },
  nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
};

interface RichTextEditorCtxType {
  urlEditorOpen: boolean;
  setUrlEditorOpen: (open: boolean) => void;
}

export const RichTextEditorCtx = createContext<RichTextEditorCtxType>({
  urlEditorOpen: false,
  setUrlEditorOpen: () => {},
});

export const RichTextEditorProvider = ({ children }: { children: React.ReactNode }) => {
  const [urlEditorOpen, setUrlEditorOpen] = useState(false);
  return (
    <RichTextEditorCtx.Provider value={{ urlEditorOpen, setUrlEditorOpen }}>
      <LexicalComposer initialConfig={initialConfig}>{children}</LexicalComposer>
    </RichTextEditorCtx.Provider>
  );
};

export const useRichTextEditor = () => {
  const [editor] = useLexicalComposerContext();
  const { urlEditorOpen, setUrlEditorOpen } = useContext(RichTextEditorCtx);

  if (!editor) {
    throw new Error('useRichTextEditor must be used within a RichTextEditorProvider');
  }

  /**
   * Checks if a serialized Lexical editor state string is empty
   */
  const isSerializedStateEmpty = useCallback(
    (serializedStateString: string): boolean => {
      if (!serializedStateString || serializedStateString.trim() === '') {
        return true;
      }

      try {
        // Parse the serialized JSON string
        const serializedState: SerializedEditorState = JSON.parse(serializedStateString);

        // Parse the editor state
        const editorState = editor.parseEditorState(serializedState);

        // Check if empty within a read callback
        return editorState.read(() => {
          return $isRootTextContentEmpty(false, true);
        });
      } catch (error) {
        // If parsing fails, consider it empty
        console.error('Error checking if serialized Lexical state is empty:', error);
        return true;
      }
    },
    [editor],
  );

  return useMemo(
    () => ({ editor, urlEditorOpen, setUrlEditorOpen, isSerializedStateEmpty }),
    [editor, urlEditorOpen, setUrlEditorOpen, isSerializedStateEmpty],
  );
};
